/** Saved catalog default when the user has not customized categories in AI settings. */
export const DEFAULT_ACTION_ITEM_CATEGORY_CATALOG = [
  "Work",
  "Personal",
  "Team follow-ups",
  "Client follow-ups",
  "Other",
];

/** Max characters for one optional category description (matches the server cap). */
export const ACTION_ITEM_CATEGORY_DESCRIPTION_MAX_CHARS = 200;

/** Chip value for “show every category” (flat list). */
export const CATEGORY_FILTER_ALL = "__all__";

/** Dispatched when the saved performer category catalog changes. */
export const ACTION_ITEM_CATEGORY_CATALOG_UPDATED_EVENT =
  "starmesh:action-item-category-catalog-updated";

/**
 * Resolve the effective saved category catalog (falls back to defaults).
 *
 * @example
 * resolveActionItemCategoryCatalog(null);
 * // ["Work", "Personal", "Team follow-ups", "Client follow-ups", "Other"]
 */
export const resolveActionItemCategoryCatalog = (catalog) =>
  Array.isArray(catalog) && catalog.length ? catalog : DEFAULT_ACTION_ITEM_CATEGORY_CATALOG;

/**
 * Append a label to a category catalog when it is not already present.
 *
 * @example
 * appendLabelToActionItemCategoryCatalog(["Work"], "Testing");
 * // { appended: true, nextCatalog: ["Work", "Testing"] }
 */
export const appendLabelToActionItemCategoryCatalog = (catalog, label) => {
  const normalized = String(label || "").trim();
  const current = resolveActionItemCategoryCatalog(catalog);
  if (!normalized) {
    return { appended: false, nextCatalog: current };
  }
  if (current.some((entry) => String(entry || "").trim().toLowerCase() === normalized.toLowerCase())) {
    return { appended: false, nextCatalog: current };
  }
  return { appended: true, nextCatalog: [...current, normalized] };
};

/**
 * Notify listeners (e.g. settings page) that the saved catalog changed.
 *
 * @example
 * notifyActionItemCategoryCatalogUpdated(["Work", "Testing"]);
 */
export const notifyActionItemCategoryCatalogUpdated = (categoryCatalog) => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(ACTION_ITEM_CATEGORY_CATALOG_UPDATED_EVENT, {
      detail: { categoryCatalog },
    })
  );
};

/**
 * Normalize the category label on an action item row.
 *
 * @example
 * getActionItemCategory({ action_category: "  Work  " });
 * // "Work"
 *
 * @example
 * getActionItemCategory({});
 * // "Other"
 */
export const getActionItemCategory = (item) => {
  const label = String(item?.action_category || "Other").trim();
  return label || "Other";
};

/**
 * Options for a category picker: saved catalog order plus the item's current label when missing.
 *
 * @example
 * buildActionItemCategoryPickerOptions(["Work", "Other"], "Legacy bucket");
 * // ["Legacy bucket", "Work", "Other"]
 */
export const buildActionItemCategoryPickerOptions = (catalog, currentLabel) => {
  const orderedCatalog = Array.isArray(catalog) && catalog.length
    ? catalog
    : DEFAULT_ACTION_ITEM_CATEGORY_CATALOG;
  const labels = orderedCatalog.map((label) => String(label || "").trim()).filter(Boolean);
  const current = String(currentLabel || "").trim();
  if (
    current &&
    !labels.some((label) => label.toLowerCase() === current.toLowerCase())
  ) {
    labels.unshift(current);
  }
  return orderCategoryLabels(labels, orderedCatalog);
};

/**
 * Sort category labels using the saved catalog order, then alphabetically.
 *
 * @example
 * orderCategoryLabels(["Other", "Work"], ["Work", "Personal", "Other"]);
 * // ["Work", "Other"]
 */
export const orderCategoryLabels = (labels, catalog) => {
  const unique = [...new Set(labels.map((l) => String(l || "").trim()).filter(Boolean))];
  const orderedCatalog = Array.isArray(catalog) ? catalog : DEFAULT_ACTION_ITEM_CATEGORY_CATALOG;
  const rank = new Map(orderedCatalog.map((label, index) => [label.toLowerCase(), index]));
  return unique.sort((a, b) => {
    const ra = rank.has(a.toLowerCase()) ? rank.get(a.toLowerCase()) : orderedCatalog.length + 1;
    const rb = rank.has(b.toLowerCase()) ? rank.get(b.toLowerCase()) : orderedCatalog.length + 1;
    if (ra !== rb) return ra - rb;
    return a.localeCompare(b, undefined, { sensitivity: "base" });
  });
};

/**
 * Build filter chip options: All + categories that appear in the current list.
 *
 * @example
 * buildCategoryChipOptions(
 *   [{ action_category: "Work" }, { action_category: "Work" }],
 *   ["Work", "Other"]
 * );
 * // [
 * //   { id: "__all__", label: "All", count: 2 },
 * //   { id: "Work", label: "Work", count: 2 },
 * // ]
 */
export const buildCategoryChipOptions = (items, catalog) => {
  const list = Array.isArray(items) ? items : [];
  const counts = {};
  for (const item of list) {
    const cat = getActionItemCategory(item);
    counts[cat] = (counts[cat] || 0) + 1;
  }
  const categories = orderCategoryLabels(Object.keys(counts), catalog);
  const chips = [
    {
      id: CATEGORY_FILTER_ALL,
      label: "All",
      count: list.length,
    },
  ];
  for (const label of categories) {
    chips.push({ id: label, label, count: counts[label] || 0 });
  }
  return chips;
};

/**
 * Filter items by a category chip selection.
 *
 * @example
 * filterItemsByCategoryFilter([{ action_category: "Work" }], "Work");
 * // [{ action_category: "Work" }]
 *
 * @example
 * filterItemsByCategoryFilter(items, CATEGORY_FILTER_ALL);
 * // same array reference semantics — returns all items
 */
export const filterItemsByCategoryFilter = (items, categoryFilter) => {
  const list = Array.isArray(items) ? items : [];
  const filter = String(categoryFilter || "").trim();
  if (!filter || filter === CATEGORY_FILTER_ALL) return list;
  return list.filter((item) => getActionItemCategory(item) === filter);
};

/**
 * Build sidebar/drawer category rows from pending items in the current source scope.
 *
 * @example
 * buildActionItemsCategoryNav(
 *   [{ status: "pending", action_category: "Work" }],
 *   ["Work", "Other"]
 * );
 * // { totalCount: 1, entries: [] } — hidden when only one category bucket
 */
export const buildActionItemsCategoryNav = (items, catalog) => {
  const list = Array.isArray(items) ? items : [];
  const pending = list.filter((item) => String(item?.status || "").trim() === "pending");
  const chips = buildCategoryChipOptions(pending, catalog);
  if (chips.length <= 1) {
    return { totalCount: pending.length, entries: [] };
  }
  return {
    totalCount: chips[0]?.count ?? pending.length,
    entries: chips.slice(1).map(({ id, label, count }) => ({ id, label, count })),
  };
};

/**
 * Build overflow-menu category filter options from the saved catalog, scoped to pending items.
 * Unlike buildActionItemsCategoryNav, keeps every catalog category visible (including zero counts).
 *
 * @example
 * buildActionItemsCategoryFilterOptions(
 *   [{ status: "pending", action_category: "Work" }],
 *   ["Work", "Other"]
 * );
 * // [
 * //   { value: "__all__", label: "All categories (1)" },
 * //   { value: "Work", label: "Work (1)" },
 * //   { value: "Other", label: "Other (0)" },
 * // ]
 */
export const buildActionItemsCategoryFilterOptions = (items, catalog) => {
  const list = Array.isArray(items) ? items : [];
  const pending = list.filter((item) => String(item?.status || "").trim() === "pending");
  const counts = {};
  for (const item of pending) {
    const category = getActionItemCategory(item);
    counts[category] = (counts[category] || 0) + 1;
  }
  const orderedCatalog = resolveActionItemCategoryCatalog(catalog);
  const labels = orderCategoryLabels(
    [...new Set([...orderedCatalog, ...Object.keys(counts)])],
    orderedCatalog
  );
  if (labels.length <= 1) return null;
  return [
    { value: CATEGORY_FILTER_ALL, label: `All categories (${pending.length})` },
    ...labels.map((label) => ({
      value: label,
      label: `${label} (${counts[label] || 0})`,
    })),
  ];
};

/**
 * True when at least one item has a non-empty account name (used for sub-grouping).
 *
 * @example
 * shouldGroupItemsByAccount([{ account: "Acme" }, { account: null }]);
 * // true
 */
export const shouldGroupItemsByAccount = (items) =>
  (Array.isArray(items) ? items : []).some((item) => String(item?.account || "").trim());

/**
 * Distinct account names from a list of action items (sorted A–Z).
 *
 * @example
 * buildActionItemAccountSuggestions([
 *   { account: "Acme" },
 *   { account: "Beta" },
 *   { account: "Acme" },
 * ]);
 * // ["Acme", "Beta"]
 */
export const buildActionItemAccountSuggestions = (items) => {
  const names = new Set();
  for (const item of Array.isArray(items) ? items : []) {
    const label = String(item?.account || "").trim();
    if (label) names.add(label);
  }
  return [...names].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
};

/**
 * Group items by account; named accounts first, then items with no account.
 *
 * @example
 * groupItemsByAccount([
 *   { account: "Acme", action_item: "A" },
 *   { account: null, action_item: "B" },
 * ]);
 * // [
 * //   { account: "Acme", items: [{ ... }] },
 * //   { account: null, items: [{ ... }] },
 * // ]
 */
export const groupItemsByAccount = (list) => {
  const order = [];
  const map = {};
  for (const item of list) {
    const account = String(item?.account || "").trim() || null;
    const key = account || "__none__";
    if (!map[key]) {
      order.push(key);
      map[key] = { account, items: [] };
    }
    map[key].items.push(item);
  }
  const named = order.filter((k) => k !== "__none__");
  const none = order.includes("__none__") ? ["__none__"] : [];
  return [...named, ...none].map((key) => map[key]);
};
